import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken, HttpErrorResponse } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../../environments/environment';

export const PUBLIC_PROPERTY_REQUEST = new HttpContextToken<boolean>(() => false);
export const normalizePublicQuery = (value: string) => value.trim().replace(/\s+/g, ' ');
export interface PublicProperty {
  id: string;
  name: string;
  location: string;
  coverImage: string;
  galleryImages: string[];
}
// Explicit projection: never retain protected fields in component state.
export function publicProperty(value: any): PublicProperty {
  if (!value || typeof value.id !== 'string' || typeof value.name !== 'string') throw new Error('Invalid public property');
  return { id: value.id, name: value.name, location: typeof value.location === 'string' ? value.location : '',
    coverImage: typeof value.coverImage === 'string' ? value.coverImage : '',
    galleryImages: Array.isArray(value.galleryImages) ? value.galleryImages.filter((v: unknown) => typeof v === 'string') : [] };
}
// Temporary adapter for the same unauthenticated featured collection used on Home.
// Swap this source for the public catalogue API when it is available.
export function carouselProperty(value: any): PublicProperty {
  const location = [value?.microMarket?.micromarketName, value?.microMarket?.cluster, value?.cityName]
    .filter(v => typeof v === 'string' && v.trim()).join(', ');
  const images = Array.isArray(value?.galleryImage)
    ? value.galleryImage.filter((v: unknown) => typeof v === 'string' && v.trim()) : [];
  return publicProperty({ id: value?._id, name: value?.name, location,
    coverImage: images[0] || '', galleryImages: images });
}
// Shuffle a copy once per preview load; exclude current/duplicate public ids.
export function discoverPublicHomes(records: PublicProperty[], currentId: string, random = Math.random): PublicProperty[] {
  const unique = [...new Map(records.filter(record => record.id !== currentId).map(record => [record.id, record])).values()];
  for (let i = unique.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [unique[i], unique[j]] = [unique[j], unique[i]];
  }
  return unique.slice(0, 3);
}
@Injectable({ providedIn: 'root' })
export class PublicPropertyService {
  private http = inject(HttpClient);
  private catalogue() {
    return this.http.get<any>(`${environment.api_URL}/dashboard/carousel`, {
      context: new HttpContext().set(PUBLIC_PROPERTY_REQUEST, true)
    }).pipe(map(res => {
      const records = res?.data?.properties;
      if (res?.status === false || !Array.isArray(records)) throw new Error('Invalid carousel response');
      return records.map(carouselProperty) as PublicProperty[];
    }));
  }
  list(q: string, page: number) {
    const query = normalizePublicQuery(q).toLowerCase();
    return this.catalogue().pipe(map(records => {
      // Filter the complete featured collection before slicing; no price/BHK/etc matching.
      const filtered = records.filter(property => [property.name, property.location]
        .some(value => normalizePublicQuery(value).toLowerCase().includes(query)));
      const start = (page - 1) * 12;
      return { data: filtered.slice(start, start + 12), total: filtered.length };
    }));
  }
  preview(id: string) {
    return this.catalogue().pipe(map(records => {
      const property = records.find(record => record.id === id);
      if (!property) throw new HttpErrorResponse({ status: 404, statusText: 'Not found' });
      return { property, related: discoverPublicHomes(records, id) };
    }));
  }
  get(id: string) {
    // Direct preview URLs resolve from the same public source; no authenticated detail fetch.
    return this.catalogue().pipe(map(records => {
      const property = records.find(record => record.id === id);
      if (!property) throw new HttpErrorResponse({ status: 404, statusText: 'Not found' });
      return property;
    }));
  }
}
